import {useCallback, useRef, useState} from 'react'

export type SelectionRestore = {
  ids: Set<number>
}

export interface TableRow {
  original: {id: number}
}

export interface TableDataItem {
  id: number
}

export interface UseRetainBulkSelectionReturn {
  isBulkActionReload: boolean
  captureSelection: (selectedRows: TableRow[]) => Set<number>
  clearRetainedSelection: () => void
  reconcileAfterReload: (tableData: TableDataItem[]) => Set<number>
  acknowledgeRestore: () => void
}

export const useRetainBulkSelection = (): UseRetainBulkSelectionReturn => {
  // Use a ref so reconcileAfterReload always reads the latest IDs
  // without depending on a stale closure over state
  const retainedIdsRef = useRef<Set<number>>(new Set())
  const [isBulkActionReload, setIsBulkActionReload] = useState(false)

  const captureSelection = useCallback((selectedRows: TableRow[]): Set<number> => {
    const ids = new Set<number>(selectedRows.map((row) => row.original.id))
    retainedIdsRef.current = ids
    setIsBulkActionReload(true)
    return ids
  }, [])

  const clearRetainedSelection = useCallback((): void => {
    retainedIdsRef.current = new Set()
    setIsBulkActionReload(false)
  }, [])

  const reconcileAfterReload = useCallback((tableData: TableDataItem[]): Set<number> => {
    const ids = retainedIdsRef.current
    if (ids.size === 0) return new Set()
    const matched = tableData.filter((row) => ids.has(row.id)).map((row) => row.id)
    return new Set(matched)
  }, [])

  const acknowledgeRestore = useCallback((): void => {
    // Reset the reload flag now that Table has been given the restore signal.
    setIsBulkActionReload(false)
  }, [])

  return {
    isBulkActionReload,
    captureSelection,
    clearRetainedSelection,
    reconcileAfterReload,
    acknowledgeRestore,
  }
}
